// © Microsoft Corporation. All rights reserved.

import { BaseExperienceConfig } from "@msnews/core";
import { SearchFormParameters } from "@msnews/search-box";
import { AutoSuggestProperties } from "@msnews/autosuggest";
import { ChildExperienceGitConfigInfo } from "@msnews/config-data-types";

/**
 * The CommonHeader config
 */
export interface CommonHeaderConfig extends BaseExperienceConfig<CommonHeaderLocalizedStrings> {
    /** Config values used for the navigation components (hamburger menu and contextual nav) */
    navigationConfig: NavigationConfig;

    /** Config values used for the search box */
    searchBoxConfig: SearchBoxConfig;

    /** Config values used for the me control */
    meControlConfig: MeControlConfig;

    /** Flag to remove the remove row of the header */
    disableFirstRow: boolean;

    /** Whether to include pcsonly query param when sending users from Prime back to Peregrine */
    includePCSOnlyFlag: boolean;

    /** Whether to handle forwarding ocid query param in current url when clicking nav item that sends the user to Prime */
    forwardOcidOnPrimeExternal: boolean;

    /** For hybrid, given a primePath (/{vertical}/{category}/{subcategory}), we'll map to the id of the expected navigation item to be selected */
    primePathToNavItemIdMap: any;

    /** For hybrid, if a match isn't found in primePathToNavItemId, we'll fallback to matching nav items by vertical with this mapping from vertical to nav item id */
    verticalToNavItemIdMap: any;

    /** Whether to delay load nav bar and hamberger menu until after TTVR is stamped */
    isDelayLoadNavAndMenuEnabled: boolean;

    /** Logo svg definitions */
    lightLogo?: string;
    darkLogo?: string;

    /**
     * Child config URLs(currently used for DesktopFeed). Setting config URLs in a href property makes CRS to download the these configs.
     * Long term solution is to rename experienceConfigHref to href in RenderInfo interface. To be done once we migrate to git configs.
     */
    childConfigHrefs?: [
        { href: string }
    ]
}

/**
 * Common header localized strings
 */
export interface CommonHeaderLocalizedStrings {
    /** Tooltip and aria label for close button */
    closeButtonTooltip: string;

    /** Tooltip and aria label for hamburger menu button */
    menuButtonTooltip: string;

    /** Search placeholder text */
    searchPlaceholderText: string;

    /** Search tooltip */
    searchTooltip?: string;

    /** Search button tooltip */
    searchButtonTooltip?: string;

    /** Navigation overflow menu button tool tip text */
    overflowButtonTooltip: string;

    /** Tooltip and aria label for the me control */
    signInTooltip?: string;

    /** Settings dialog title */
    settingsDialogTitle?: string;

    /** Language dropdown label */
    languageDropdownLabel?: string;

    /** Settings dialog personalize button label */
    settingsDialogPersonalizeLabel?: string;

    /** Settings dialog personalize button text */
    settingsDialogPersonalizeButtonText?: string;

    /** Settings button tooltip */
    settingsButtonTooltip?: string;

    /** Settings dialog close menu tooltip */
    settingsCloseButtonTooltip?: string;

    /** Personalize button text */
    personalizeButtonText?: string;

    /** Localized strings for navigation items */
    myFeed?: string;
    myInterests?: string;
    mySaves?: string;
    history?: string;
    experienceSettings?: string;

    /** Microsoft logo alt text */
    logoAltText?: string;
}

/**
 * Values used for navigation components
 */
export interface NavigationConfig {
    /** List of items in the hamburger menu */
    hamburgerMenuItems: NavigationItem[];

    /** Home navigation item (for contextual nav) */
    homeNavigationItem: NavigationItem;

    /** Secondary navigation item (for contextual nav) */
    secondaryNavigationItem: NavigationItem;

    /** Contextual nav items */
    contextualNavItems: NavigationItem[];

    /** Nav item for personalize button */
    interestsNavigationItem: NavigationItem;

    /** Flag to enable old msn routing structure - prefix routes with locale and vertical ("feed") /${localeCode}/feed */
    enableLocaleFeedPrefix: boolean;

    /**
     * Value should be true if client side routing should be forced for News vertical.
     * PS: Once this feature is enabled for more verticals, it should change to a more general name.
    */
    shouldForceRouterForNews?: boolean;
}

/**
 * Interface for a navigation item
 */
export interface NavigationItem {
    /** Item's ID */
    id: string;

    /** Display string to be used */
    display: string;

    /** Display key to identify loc string */
    displayKey?: string;

    /** Name of icon to be used with navigation item */
    iconName?: string;

    /** List of child navigation items */
    children?: NavigationItem[];

    /** Flag for if this item should exhibit requery behavior (used for contextual nav) */
    shouldRequery?: boolean;

    /** Object for rendering info */
    renderInfo?: NavigationItemRenderInfo;

    /** Object for telemetry values */
    telemetryInfo?: NavigationItemTelemetryInfo;

    /** Flag to show divider after this nav item */
    showDivider?: boolean;

    /** Vertical Key the item belongs to, if belongs to any */
    verticalKey?: string;

    /** Category Key the item belongs to, if belongs to any */
    categoryKey?: string;

    /** SubCategory Key the item belongs to, if belongs to any */
    subCategoryKey?: string;

    /** Page title to show up on pivot change */
    pageTitle?: string;
}

/**
 * Interface for the navigation render info
 */
export interface NavigationItemRenderInfo {
    /** Indicate how the navigation item's content should be rendered */
    renderType?: RenderType;

    /** Route path to be used for rendering with router */
    path?: string;

    /** Flag to leave the path unformatted. Formatted paths will be of the form /interest/${path} */
    usePathNameAsIs?: boolean;

    /** Experience config href to be used for rendering with pivot manager */
    experienceConfigHref?: string;

    /**
     * Experience config ref for git configs
     *
     * TODO (31683613): rename to clarify that this is for git configs
     */
    experienceConfigRef?: ChildExperienceGitConfigInfo;

    /** Context for rendering navigation item */
    context?: { feedId: string };

    /** External url to navigate to when the navigation item is clicked */
    externalUrl?: string;
}

/**
 * Identify how the navigation item's content should be rendered
 */
export enum RenderType {
    Router = 0,
    ExternalLink = 2,
    PrimeExternalLink = 3
}

/**
 * Interface for the navigation item telemetry
 */
export interface NavigationItemTelemetryInfo {
    /** Page name for given navigation item */
    pageName?: string;

    /** Category for given navigation item */
    pivotCategory?: string;
}

/**
 * Values used for search box
 */
export interface SearchBoxConfig {
    /** Search form codes */
    formCodes: SearchBoxFormCodes;

    /** Autosuggest properties */
    autoSuggest?: AutoSuggestProperties;

    /** Search button text */
    searchButtonText?: string;

    /** Flag to enable search button logo */
    enableSearchButtonLogo?: boolean;

    /** Enable search focus */
    enableSearchFormFocus?: boolean;

    /** Should initialize auto suggest (only applicable if AutoSuggestDelayBinding is set to No Binding, i.e. on-demand binding) */
    shouldInitializeAutoSuggest?: boolean;

    /** Search input text field contract */
    input: SearchInput;

    /** Search action url */
    searchActionUrl?: string;

    /** Search form target */
    target?: string;

    /** List of search form parameters */
    formParameters: Array<SearchFormParameters>

    /** Enable key press capture */
    enableKeyPressCapture?: boolean;
}

/**
 * Search form codes
 */
export interface SearchBoxFormCodes {
    /** Kids mode form code */
    childMode?: string;

    /** Content based form codes */
    contentBased?: {
        /** Form code when using bing search enginer */
        bingSearchEngine: ContentBasedFormCode;

        /** Form code when not using bing */
        nonBingSearchEngine: ContentBasedFormCode;
    };

    /** Co brand form code */
    coBrand?: string;
}

/**
 * Form codes based on content ON/OFF state
 */
export interface ContentBasedFormCode {
    /** Content ON form code value */
    contentOn?: string;

    /** Content OFF form code value */
    contentOff?: string;

    /** Blank page form code value */
    blankPage?: string;
}

/**
 * Interface to define Search input text field properties
 */
export interface SearchInput {
    /** The Search with Bing placeholder text */
    bingPlaceholderText?: string;

    /** Placeholder text for business search results */
    businessResultPlaceholderText?: BusinessSearchPlaceholderText;

    /** Placeholder text enabled */
    placeholderTextEnabled?: boolean;

    /** Default placeholder text */
    placeholderText?: string;
}

/**
 * Interface to define business search related placeholder text values
 */
export interface BusinessSearchPlaceholderText {
    /** Placeholder text when tenant name is available and default search provider is bing */
    tenantAvailable: string;

    /** Placeholder text when tenant name is available and default search provider is non-bing */
    nonBingTenantAvailable: string;

    /** Placeholder text when tenant name is not available and default search provider is bing */
    tenantMissing: string;

    /** Placeholder text when tenant name is not available and default search provider is non-bing */
    nonBingTenantMissing: string;
}

/**
 * Values used for me control
 */
export interface MeControlConfig {
    /** Partner ID provided by the Me Control team, unique for each app-type */
    partnerId: string;
}